import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getManufacturingMaterialCostingSummary,
  listManufacturingMaterials,
  type MfgMaterial,
} from "@/lib/manufacturingClient";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";

/**
 * Materials list + WAC costing snapshot. Used by Inventory, Receive, and
 * Models panels — each calls this independently so per-mode mounts stay
 * lightweight and panel-local refresh buttons flow through `refresh()`.
 *
 * `enabled` short-circuits the fetch: panels pass the entitlement-gate
 * `enabled` flag through so we don't hit `/api/mfg/materials` for orgs
 * without the add-on.
 */
export function useManufacturingMaterials(opts: { enabled: boolean }) {
  const { enabled } = opts;
  const scopedOrganizationId = useScopedOrganizationId();
  const [materials, setMaterials] = useState<MfgMaterial[]>([]);
  const [costingLastUpdatedAt, setCostingLastUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (mode?: { refresh?: boolean }) => {
      if (!enabled) {
        setMaterials([]);
        setCostingLastUpdatedAt(null);
        setLoading(false);
        setWorking(false);
        return;
      }
      if (mode?.refresh) setWorking(true);
      else setLoading(true);
      setError(null);

      const [list, summary] = await Promise.all([
        listManufacturingMaterials(),
        getManufacturingMaterialCostingSummary(),
      ]);

      if (!list.ok) {
        setMaterials([]);
        setCostingLastUpdatedAt(null);
        setError(list.error);
        setLoading(false);
        setWorking(false);
        return;
      }

      setMaterials(Array.isArray(list.data.materials) ? list.data.materials : []);
      if (summary.ok) {
        const latest = Array.isArray(summary.data.summary)
          ? summary.data.summary
              .map((row) =>
                typeof row.last_costed_at === "string" ? Date.parse(row.last_costed_at) : Number.NaN
              )
              .filter((ts) => Number.isFinite(ts))
              .sort((a, b) => b - a)[0]
          : undefined;
        setCostingLastUpdatedAt(typeof latest === "number" ? new Date(latest).toISOString() : null);
      } else {
        setCostingLastUpdatedAt(null);
      }
      setLoading(false);
      setWorking(false);
    },
    [enabled]
  );

  useEffect(() => {
    void load();
  }, [load, scopedOrganizationId]);

  const inventoryValue = useMemo(
    () => materials.reduce((sum, m) => sum + (Number(m.unit_cost) || 0) * (Number(m.on_hand_qty) || 0), 0),
    [materials]
  );

  return {
    materials,
    costingLastUpdatedAt,
    inventoryValue,
    loading,
    working,
    error,
    refresh: () => void load({ refresh: true }),
  };
}
